import React, { useState, useCallback } from "react";
import {
  Clock, Users, CheckCircle, BarChart3, Lock, Star,
  ChevronDown, ChevronUp, MessageSquare, ArrowRight, Check,
} from "lucide-react";
import theme from "../../styles/theme";
import { WizardHatIcon } from "../common/Brand";
import { castVote } from "../../api";

const T = theme.colors;

const CATEGORY_CONFIG = {
  next_analysis: { color: "#f59e0b", label: "NEXT ANALYSIS", icon: BarChart3 },
  sentiment: { color: "#60a5fa", label: "SENTIMENT", icon: MessageSquare },
  priority: { color: "#ef4444", label: "PRIORITY", icon: Star },
  experience: { color: "#4ade80", label: "EXPERIENCE", icon: Users },
  policy: { color: "#a855f7", label: "POLICY", icon: Lock },
};

const RATING_COLORS = ["#ef4444", "#f97316", "#f59e0b", "#4ade80", "#22c55e"];

function recalcPercentages(options) {
  const total = options.reduce((s, o) => s + (o.votes || 0), 0);
  return options.map((o) => ({
    ...o,
    percentage: total > 0 ? Math.round(((o.votes || 0) / total) * 1000) / 10 : 0,
  }));
}

export default function SurveyCard({ survey: initial, onUpdate }) {
  const [options, setOptions] = useState(() => initial.options || []);
  const [hasVoted, setHasVoted] = useState(() =>
    initial.hasVoted || (initial.options || []).some((o) => o.selected)
  );
  const [totalVotes, setTotalVotes] = useState(initial.totalVotes || 0);
  const [showInterpretation, setShowInterpretation] = useState(false);

  const cat = CATEGORY_CONFIG[initial.category] || CATEGORY_CONFIG.sentiment;
  const CatIcon = cat.icon;
  const isClosed = initial.status === "closed" || initial.isExpired;
  const questionType = initial.question_type || (initial.allow_multiple ? "multiple" : "single");
  const isRating = questionType === "rating";
  const isMultiple = questionType === "multiple" || initial.allow_multiple;
  const showResults = isClosed || (initial.show_results_before_close !== false && hasVoted);

  // ── Vote handler — local state is source of truth ──────────────────────────

  const handleVote = useCallback((optionId) => {
    if (isClosed) return;

    setOptions((prev) => {
      let updated;

      if (isMultiple) {
        // Toggle the clicked option, leave others alone
        updated = prev.map((o) => {
          if (o.id === optionId) {
            const nowSelected = !o.selected;
            return {
              ...o,
              selected: nowSelected,
              votes: nowSelected ? (o.votes || 0) + 1 : Math.max(0, (o.votes || 0) - 1),
            };
          }
          return o;
        });
      } else {
        // Single / rating: deselect previous, select new (or toggle off)
        const clickedWasSelected = prev.find((o) => o.id === optionId)?.selected;

        updated = prev.map((o) => {
          if (o.id === optionId) {
            const nowSelected = !clickedWasSelected;
            return {
              ...o,
              selected: nowSelected,
              votes: nowSelected ? (o.votes || 0) + 1 : Math.max(0, (o.votes || 0) - 1),
            };
          }
          // Deselect any previously selected
          if (o.selected) {
            return { ...o, selected: false, votes: Math.max(0, (o.votes || 0) - 1) };
          }
          return o;
        });
      }

      const recalced = recalcPercentages(updated);
      const newTotal = recalced.reduce((s, o) => s + (o.votes || 0), 0);
      setTotalVotes(newTotal);
      setHasVoted(recalced.some((o) => o.selected));
      return recalced;
    });

    // Fire API in background — don't touch local state with result
    castVote(initial.id, optionId).catch((err) =>
      console.error("Vote API error (local state preserved):", err)
    );
  }, [initial.id, isClosed, isMultiple]);

  // ── Derived data ───────────────────────────────────────────────────────────

  const maxVotes = Math.max(...options.map((o) => o.votes || 0), 0);
  const avgRating =
    isRating && totalVotes > 0
      ? options.reduce((sum, o, i) => sum + (i + 1) * (o.votes || 0), 0) / totalVotes
      : 0;

  return (
    <div
      className="fade-in"
      style={{
        background: theme.gradients.card,
        border: `1px solid ${initial.is_featured ? cat.color + "50" : T.border}`,
        borderRadius: 16,
        overflow: "hidden",
      }}
    >
      {/* Featured banner */}
      {initial.is_featured && (
        <div
          style={{
            background: `linear-gradient(90deg, ${cat.color}30, transparent)`,
            padding: "6px 16px",
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            color: cat.color,
            fontFamily: theme.fonts.mono,
            letterSpacing: 1,
          }}
        >
          <Star size={11} fill={cat.color} /> FEATURED — YOUR VOTE DECIDES THE NEXT
          ANALYSIS
        </div>
      )}

      {/* Header */}
      <div style={{ padding: "20px 24px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: theme.gradients.hero,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `2px solid ${T.purple}`,
            }}
          >
            <WizardHatIcon size={18} color="#fff" />
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                Wyzcon Intelligence
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  fontSize: 9,
                  padding: "2px 6px",
                  borderRadius: 4,
                  background: T.purple + "30",
                  color: T.violet,
                  fontWeight: 700,
                  fontFamily: theme.fonts.mono,
                }}
              >
                <CheckCircle size={9} /> VERIFIED
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 11,
                color: T.textDim,
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 3,
                  color: isClosed ? T.textDim : cat.color,
                }}
              >
                <Clock size={10} /> {initial.timeLeft}
              </span>
              <span>·</span>
              <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Users size={10} /> {initial.uniqueVoters || 0} voters
              </span>
            </div>
          </div>

          {/* Badges */}
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            {isRating && (
              <span
                style={{
                  fontSize: 9,
                  padding: "3px 6px",
                  borderRadius: 4,
                  background: "#f59e0b20",
                  color: "#f59e0b",
                  fontFamily: theme.fonts.mono,
                  fontWeight: 600,
                }}
              >
                RATE
              </span>
            )}
            {isMultiple && (
              <span
                style={{
                  fontSize: 9,
                  padding: "3px 6px",
                  borderRadius: 4,
                  background: "#60a5fa20",
                  color: "#60a5fa",
                  fontFamily: theme.fonts.mono,
                  fontWeight: 600,
                }}
              >
                MULTI
              </span>
            )}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 10,
                padding: "3px 8px",
                borderRadius: 4,
                background: cat.color + "20",
                color: cat.color,
                fontFamily: theme.fonts.mono,
                fontWeight: 600,
                letterSpacing: 1,
              }}
            >
              <CatIcon size={11} /> {cat.label}
            </span>
          </div>
        </div>

        {/* Title & description */}
        <h3
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#fff",
            margin: "0 0 6px",
            lineHeight: 1.35,
          }}
        >
          {initial.title}
        </h3>
        {initial.description && (
          <p
            style={{
              fontSize: 13,
              color: T.textMuted,
              lineHeight: 1.6,
              margin: "0 0 16px",
            }}
          >
            {initial.description}
          </p>
        )}

        {/* Average rating display */}
        {isRating && showResults && avgRating > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 16px",
              marginBottom: 16,
              borderRadius: 10,
              background: T.surface + "80",
              border: `1px solid ${T.border}`,
            }}
          >
            <div
              style={{
                fontSize: 32,
                fontWeight: 800,
                color: RATING_COLORS[Math.round(avgRating) - 1] || "#f59e0b",
                fontFamily: theme.fonts.mono,
                lineHeight: 1,
              }}
            >
              {avgRating.toFixed(1)}
            </div>
            <div>
              <div style={{ display: "flex", gap: 3 }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    size={16}
                    fill={n <= Math.round(avgRating) ? "#f59e0b" : "transparent"}
                    color={n <= Math.round(avgRating) ? "#f59e0b" : T.textDim + "40"}
                    strokeWidth={1.5}
                  />
                ))}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: T.textDim,
                  marginTop: 2,
                  fontFamily: theme.fonts.mono,
                }}
              >
                Average from {initial.uniqueVoters || 0} responses
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Options ─────────────────────────────────────────────────────────── */}
      <div style={{ padding: "0 24px 20px" }}>
        {options.map((option, idx) => {
          const isSelected = option.selected;
          const isWinner = isClosed && option.votes === maxVotes && maxVotes > 0;
          const pct = option.percentage || 0;
          const ratingColor = isRating ? RATING_COLORS[idx] || cat.color : cat.color;
          const optionColor = isRating ? ratingColor : cat.color;

          return (
            <div
              key={option.id}
              onClick={() => handleVote(option.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleVote(option.id);
                }
              }}
              style={{
                width: "100%",
                padding: isRating ? "10px 16px" : "12px 16px",
                marginBottom: 8,
                borderRadius: 10,
                border: `1px solid ${
                  isSelected
                    ? optionColor + "70"
                    : isWinner
                    ? "#4ade8050"
                    : T.border
                }`,
                background: isSelected ? optionColor + "12" : T.surface + "80",
                cursor: isClosed ? "default" : "pointer",
                textAlign: "left",
                position: "relative",
                overflow: "hidden",
                transition: "all 0.2s",
                outline: "none",
                userSelect: "none",
              }}
            >
              {/* Result bar fill */}
              {showResults && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: `${pct}%`,
                    background: isWinner
                      ? "#4ade8015"
                      : isSelected
                      ? optionColor + "15"
                      : T.textDim + "08",
                    transition: "width 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                    borderRadius: 10,
                    pointerEvents: "none",
                  }}
                />
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {/* ── Selection indicator per type ── */}
                {isRating ? (
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: isSelected ? ratingColor : "transparent",
                      border: `2px solid ${isSelected ? ratingColor : T.textDim + "40"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.2s",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: isSelected ? "#fff" : T.textDim,
                        fontFamily: theme.fonts.mono,
                      }}
                    >
                      {idx + 1}
                    </span>
                  </div>
                ) : isMultiple ? (
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      border: `2px solid ${isSelected ? optionColor : T.textDim + "50"}`,
                      background: isSelected ? optionColor : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.2s",
                    }}
                  >
                    {isSelected && <Check size={12} color="#fff" strokeWidth={3} />}
                  </div>
                ) : (
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      border: `2px solid ${isSelected ? optionColor : T.textDim + "50"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.2s",
                    }}
                  >
                    {isSelected && (
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: optionColor,
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Option text */}
                <span
                  style={{
                    flex: 1,
                    fontSize: 13,
                    color: isSelected ? "#fff" : T.text,
                    fontWeight: isSelected || isWinner ? 600 : 400,
                    lineHeight: 1.4,
                  }}
                >
                  {option.text}
                </span>

                {/* Percentage */}
                {showResults && (
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      fontFamily: theme.fonts.mono,
                      color: isWinner
                        ? "#4ade80"
                        : isSelected
                        ? optionColor
                        : T.textMuted,
                      minWidth: 42,
                      textAlign: "right",
                    }}
                  >
                    {pct}%
                  </span>
                )}

                {/* Vote count */}
                {showResults && (
                  <span
                    style={{
                      fontSize: 10,
                      color: T.textDim,
                      fontFamily: theme.fonts.mono,
                      minWidth: 20,
                      textAlign: "right",
                    }}
                  >
                    ({option.votes || 0})
                  </span>
                )}

                {/* Winner badge */}
                {isWinner && isClosed && (
                  <span
                    style={{
                      fontSize: 8,
                      padding: "2px 5px",
                      borderRadius: 3,
                      background: "#4ade8025",
                      color: "#4ade80",
                      fontWeight: 700,
                      fontFamily: theme.fonts.mono,
                    }}
                  >
                    TOP
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {/* Footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 8,
            fontSize: 11,
            color: T.textDim,
            fontFamily: theme.fonts.mono,
          }}
        >
          <span>
            {totalVotes} vote{totalVotes !== 1 ? "s" : ""}
            {!isClosed &&
              !hasVoted &&
              (isMultiple
                ? " · Select all that apply"
                : isRating
                ? " · Click to rate"
                : " · Click to vote")}
            {!isClosed &&
              hasVoted &&
              (isMultiple ? " · Click to add/remove" : " · Click to change")}
          </span>
          {!isClosed && (
            <span style={{ color: cat.color }}>{initial.timeLeft}</span>
          )}
        </div>
      </div>

      {/* ── Wyzcon Interpretation (closed surveys) ─────────────────────────── */}
      {isClosed && initial.interpretation && (
        <div style={{ borderTop: `1px solid ${T.border}` }}>
          <div
            onClick={() => setShowInterpretation(!showInterpretation)}
            role="button"
            tabIndex={0}
            style={{
              width: "100%",
              padding: "12px 24px",
              background: "transparent",
              border: "none",
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: theme.gradients.hero,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${T.purple}60`,
              }}
            >
              <WizardHatIcon size={11} color="#fff" />
            </div>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: T.violet,
                fontFamily: theme.fonts.mono,
                letterSpacing: 0.5,
              }}
            >
              WYZCON INTERPRETATION
            </span>
            <div style={{ flex: 1 }} />
            {showInterpretation ? (
              <ChevronUp size={14} color={T.textDim} />
            ) : (
              <ChevronDown size={14} color={T.textDim} />
            )}
          </div>

          {showInterpretation && (
            <div style={{ padding: "0 24px 20px" }}>
              <div
                style={{
                  padding: "16px 20px",
                  borderRadius: 10,
                  background: T.purple + "08",
                  border: `1px solid ${T.purple}20`,
                  fontSize: 13,
                  color: T.text,
                  lineHeight: 1.7,
                }}
              >
                {initial.interpretation}
              </div>
              {initial.linked_post && (
                <div
                  style={{
                    marginTop: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 11,
                    color: T.violet,
                    cursor: "pointer",
                  }}
                >
                  <ArrowRight size={12} />
                  <span>Read the full analysis</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
